import { supabase } from "./supabase";

function safeText(value) {
  return String(value ?? "").trim();
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function uniqueByUrl(documents) {
  const seen = new Set();
  return (documents || []).filter((document) => {
    const url = safeText(document?.url);
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

export function normalizeBillingOcDocuments(value, legacy = {}) {
  const normalized = asArray(value)
    .map((document, index) => {
      const url = safeText(document?.file_url || document?.url);
      if (!url) return null;
      const number = safeText(document?.oc_number || document?.number);
      const fileName = safeText(document?.file_name || document?.name);
      return {
        id: safeText(document?.id) || `oc-${index + 1}`,
        type: "oc",
        url,
        number: number || null,
        fileName: fileName || null,
        label: number
          ? `OC ${number}`
          : fileName || `Orden de compra ${index + 1}`,
        createdAt: document?.created_at || document?.updated_at || null,
      };
    })
    .filter(Boolean);

  const legacyUrl = safeText(legacy?.url);
  if (legacyUrl) {
    const legacyNumber = safeText(legacy?.number);
    normalized.push({
      id: "oc-legacy",
      type: "oc",
      url: legacyUrl,
      number: legacyNumber || null,
      fileName: null,
      label: legacyNumber ? `OC ${legacyNumber}` : "Orden de compra",
      createdAt: null,
    });
  }

  return uniqueByUrl(normalized).map((document, index) => ({
    ...document,
    label:
      document.label === "Orden de compra" && normalized.length > 1
        ? `Orden de compra ${index + 1}`
        : document.label,
  }));
}

export function normalizeBillingRemitoDocuments(value, legacyUrl = null) {
  const normalized = asArray(value)
    .map((remito, index) => {
      const url = safeText(remito?.pdf_url || remito?.file_url || remito?.url);
      if (!url) return null;
      const number = safeText(remito?.official_number || remito?.code || remito?.number);
      return {
        id: safeText(remito?.id) || `remito-${index + 1}`,
        type: "remito",
        url,
        number: number || null,
        fileName: safeText(remito?.file_name) || null,
        label: number ? `Remito ${number}` : `Remito ${index + 1}`,
        date: remito?.date_out || null,
        createdAt: remito?.created_at || remito?.updated_at || null,
      };
    })
    .filter(Boolean);

  const fallbackUrl = safeText(legacyUrl);
  if (fallbackUrl) {
    normalized.push({
      id: "remito-legacy",
      type: "remito",
      url: fallbackUrl,
      number: null,
      fileName: null,
      label: "Remito",
      date: null,
      createdAt: null,
    });
  }

  const unique = uniqueByUrl(normalized);
  return unique.map((document, index) => ({
    ...document,
    label:
      document.label === "Remito" && unique.length > 1
        ? `Remito ${index + 1}`
        : document.label,
  }));
}

export async function loadBillingAttachments({
  osId = null,
  poId = null,
  offerId = null,
  fallbackOcDocuments = [],
  fallbackOcUrl = null,
  fallbackOcNumber = null,
  fallbackRemitoDocuments = [],
  fallbackRemitoUrl = null,
} = {}) {
  let resolvedOfferId = offerId || null;
  let poIds = poId ? [poId] : [];
  let osIds = osId ? [osId] : [];
  let offerRow = null;
  let remitoRows = [];

  try {
    if (!resolvedOfferId && poId) {
      const { data, error } = await supabase
        .from("purchase_orders")
        .select("offer_id")
        .eq("id", poId)
        .maybeSingle();
      if (error) throw error;
      resolvedOfferId = data?.offer_id || null;
    }

    if (resolvedOfferId) {
      const { data, error } = await supabase
        .from("purchase_orders")
        .select("id")
        .eq("offer_id", resolvedOfferId)
        .limit(200);
      if (error) throw error;
      poIds = Array.from(
        new Set([...(poIds || []), ...(data || []).map((row) => row?.id)].filter(Boolean))
      );
    }
  } catch (error) {
    console.warn("[billing-attachments] purchase orders", error);
  }

  try {
    if (resolvedOfferId) {
      const { data, error } = await supabase
        .from("offers")
        .select("id, oc_number, oc_pdf_url, oc_documents")
        .eq("id", resolvedOfferId)
        .maybeSingle();
      if (error) throw error;
      offerRow = data || null;
    }
  } catch (error) {
    console.warn("[billing-attachments] offer documents", error);
  }

  try {
    if (poIds.length) {
      const { data, error } = await supabase
        .from("service_orders")
        .select("id")
        .in("po_id", poIds)
        .limit(1000);
      if (error) throw error;
      osIds = Array.from(
        new Set([...(osIds || []), ...(data || []).map((row) => row?.id)].filter(Boolean))
      );
    }

    if (osIds.length) {
      const { data, error } = await supabase
        .from("remitos")
        .select("id, os_id, code, official_number, date_out, pdf_url, created_at, updated_at")
        .in("os_id", osIds)
        .order("date_out", { ascending: false })
        .limit(1000);
      if (error) throw error;
      remitoRows = data || [];
    }
  } catch (error) {
    console.warn("[billing-attachments] remito documents", error);
  }

  const ocDocuments = normalizeBillingOcDocuments(
    offerRow?.oc_documents ?? fallbackOcDocuments,
    {
      url: offerRow?.oc_pdf_url || fallbackOcUrl,
      number: offerRow?.oc_number || fallbackOcNumber,
    }
  );
  const remitoDocuments = normalizeBillingRemitoDocuments(
    remitoRows.length ? remitoRows : fallbackRemitoDocuments,
    fallbackRemitoUrl
  );

  return {
    ocDocuments,
    remitoDocuments,
    offerId: resolvedOfferId,
    poIds,
    osIds,
  };
}

export async function loadBillingAttachmentsBatch(rows = []) {
  const input = Array.isArray(rows) ? rows : [];
  if (!input.length) return [];

  let poIds = Array.from(new Set(input.map((row) => row?.po_id).filter(Boolean)));
  const initialOsIds = Array.from(new Set(input.map((row) => row?.os_id).filter(Boolean)));
  const poToOffer = new Map();
  const offerToPoIds = new Map();
  const offerById = new Map();
  const osToPo = new Map();
  const remitosByPo = new Map();
  const remitosByOs = new Map();

  try {
    if (poIds.length) {
      const { data, error } = await supabase
        .from("purchase_orders")
        .select("id, offer_id")
        .in("id", poIds);
      if (error) throw error;
      for (const row of data || []) {
        if (row?.id && row?.offer_id) poToOffer.set(row.id, row.offer_id);
      }

      const offerIds = Array.from(new Set([...poToOffer.values()].filter(Boolean)));
      if (offerIds.length) {
        const { data: relatedPoRows, error: relatedPoError } = await supabase
          .from("purchase_orders")
          .select("id, offer_id")
          .in("offer_id", offerIds)
          .limit(1000);
        if (relatedPoError) throw relatedPoError;
        for (const row of relatedPoRows || []) {
          if (!row?.id || !row?.offer_id) continue;
          poToOffer.set(row.id, row.offer_id);
          if (!offerToPoIds.has(row.offer_id)) offerToPoIds.set(row.offer_id, []);
          offerToPoIds.get(row.offer_id).push(row.id);
        }
        poIds = Array.from(
          new Set([...poIds, ...(relatedPoRows || []).map((row) => row?.id)].filter(Boolean))
        );

        const { data: offerRows, error: offerError } = await supabase
          .from("offers")
          .select("id, oc_number, oc_pdf_url, oc_documents")
          .in("id", offerIds);
        if (offerError) throw offerError;
        for (const row of offerRows || []) {
          if (row?.id) offerById.set(row.id, row);
        }
      }
    }
  } catch (error) {
    console.warn("[billing-attachments] batch offers", error);
  }

  try {
    let osIds = [...initialOsIds];
    if (poIds.length) {
      const { data, error } = await supabase
        .from("service_orders")
        .select("id, po_id")
        .in("po_id", poIds)
        .limit(5000);
      if (error) throw error;
      for (const row of data || []) {
        if (row?.id && row?.po_id) osToPo.set(row.id, row.po_id);
      }
      osIds = Array.from(
        new Set([...osIds, ...(data || []).map((row) => row?.id)].filter(Boolean))
      );
    }

    if (osIds.length) {
      const { data, error } = await supabase
        .from("remitos")
        .select("id, os_id, code, official_number, date_out, pdf_url, created_at, updated_at")
        .in("os_id", osIds)
        .order("date_out", { ascending: false })
        .limit(5000);
      if (error) throw error;
      for (const remito of data || []) {
        if (!remito?.os_id) continue;
        if (!remitosByOs.has(remito.os_id)) remitosByOs.set(remito.os_id, []);
        remitosByOs.get(remito.os_id).push(remito);
        const relatedPoId = osToPo.get(remito.os_id);
        if (!relatedPoId) continue;
        if (!remitosByPo.has(relatedPoId)) remitosByPo.set(relatedPoId, []);
        remitosByPo.get(relatedPoId).push(remito);
      }
    }
  } catch (error) {
    console.warn("[billing-attachments] batch remitos", error);
  }

  return input.map((row) => {
    const relatedOfferId = poToOffer.get(row?.po_id) || null;
    const offer = relatedOfferId ? offerById.get(relatedOfferId) || null : null;
    const relatedPoIds = relatedOfferId
      ? offerToPoIds.get(relatedOfferId) || [row?.po_id].filter(Boolean)
      : [row?.po_id].filter(Boolean);
    const remitoRows = [
      ...relatedPoIds.flatMap((relatedPoId) => remitosByPo.get(relatedPoId) || []),
      ...(remitosByOs.get(row?.os_id) || []),
    ];

    return {
      ocDocuments: normalizeBillingOcDocuments(
        offer?.oc_documents ?? row?.oc_documents ?? [],
        {
          url: offer?.oc_pdf_url || row?.oc_pdf_url || null,
          number: offer?.oc_number || row?.oc_number || row?.accept_reference || null,
        }
      ),
      remitoDocuments: normalizeBillingRemitoDocuments(
        remitoRows.length ? remitoRows : row?.remito_documents || [],
        row?.remito_pdf_url || null
      ),
    };
  });
}
